import first from "lodash/first";
import { type FunctionComponent, useRef } from "react";
import { graphql, useFragment } from "react-relay";

import { StyledImage } from "~/components/article/article-image/styles";
import type { DefaultComponentsProvider } from "~/components/article/article-render";
import { Headline } from "~/components/article/article-render/generic-article/styles";
import { useImageUtils } from "~/lib/hooks";
import type { headerOpinionArticle$key } from "~/queries/__generated__/headerOpinionArticle.graphql";

import { useOpinionHeaderObserver } from "./hooks";
import {
  Author,
  AuthorHeadContentContainer,
  AuthorHeaderContent,
  AuthorNames,
  ColumnistProfilePictureContainer,
  Container,
  HeaderContainer,
  Label,
  OpinionAuthorBlockDesktopBlock,
  OpinionAuthorHeadContainer,
  StyledContentSchemaRender,
  StyledHeadline,
  StyledOpinionIcon,
  StyledPlaceholder,
  StyledSubHeadline,
  StyledTooltip,
  TooltipContainer,
  TooltipHeading,
  TypeName,
} from "./styles";

type Props = {
  className?: string;
  isColumnistArticle: boolean;
  isEditorialOpinionType: boolean;
  provider: DefaultComponentsProvider;
  reference: headerOpinionArticle$key;
};

export const OpinionHeader: FunctionComponent<Props> = ({
  className,
  isColumnistArticle,
  isEditorialOpinionType,
  provider,
  reference: reference_,
}) => {
  const article = useFragment(
    graphql`
      fragment headerOpinionArticle on Article {
        types {
          value {
            name
            description {
              json
            }
          }
        }
        authors {
          name
          image(filter: { type: AUTHOR_IMAGE }) {
            url
          }
          authorLarge: image(filter: { type: AUTHOR_LARGE }) {
            url
          }
        }
        ...hooksHeaderOpinionArticle
        ...contentSchemaRenderContent
      }
    `,
    reference_,
  );

  const containerReference = useRef<HTMLDivElement>(null);
  useOpinionHeaderObserver(article, containerReference);

  const { getResizedImageUrl } = useImageUtils();
  const mainAuthor = first(article.authors);
  if (!mainAuthor) return null;

  const renderOpinionAuthorBlock = () => {
    const authorNames = article.authors
      ?.map(author => author && author.name)
      .join(", ")
      .replaceAll(/,(?!.*,)/gim, " and ");

    // Display Author names if columnist
    if (!isEditorialOpinionType) return <AuthorNames>{authorNames}</AuthorNames>;

    /*
      Display type name if editorial opinion
      1st element of types arrays is always column type
      2nd element of types arrays is always Editorial type
     */
    const [_, second] = article.types?.flatMap(type => type?.value) ?? [];
    return (
      <>
        <TypeName>{second?.name ?? "Opinion"}</TypeName>
        <AuthorNames> by {authorNames}</AuthorNames>
      </>
    );
  };

  const renderColumnistProfilePicture = () => {
    if (!isColumnistArticle) return null;

    const imageUrl = mainAuthor.authorLarge?.url ?? mainAuthor.image?.url;
    return (
      <ColumnistProfilePictureContainer>
        {imageUrl ? (
          <StyledImage
            $aspectRatio="1/1"
            $objectFit="cover"
            alt={mainAuthor.name}
            lazyload={false}
            src={getResizedImageUrl(imageUrl, 772)}
          />
        ) : (
          <StyledPlaceholder />
        )}
      </ColumnistProfilePictureContainer>
    );
  };

  const renderArticleTypeTooltip = () => {
    const [mainType] = first(article?.types ?? [])?.value ?? [];

    return (
      <>
        <StyledTooltip
          placement="bottom-start"
          title={
            <TooltipContainer>
              <TooltipHeading>Opinion </TooltipHeading>
              <StyledContentSchemaRender reference={article} schema={mainType?.description?.json} />
            </TooltipContainer>
          }
          arrow
        >
          <Label>{isColumnistArticle ? "SCMP Columnist" : "Opinion"}</Label>
        </StyledTooltip>
      </>
    );
  };

  return (
    <Container
      $isColumnistArticle={isColumnistArticle}
      className={className}
      ref={containerReference}
    >
      {renderColumnistProfilePicture()}
      <HeaderContainer>
        <OpinionAuthorHeadContainer>
          <StyledOpinionIcon />
          <AuthorHeadContentContainer>
            {renderArticleTypeTooltip()}
            <AuthorHeaderContent>{renderOpinionAuthorBlock()}</AuthorHeaderContent>
          </AuthorHeadContentContainer>
        </OpinionAuthorHeadContainer>
        <StyledHeadline>
          <OpinionAuthorBlockDesktopBlock>
            {renderOpinionAuthorBlock()}
          </OpinionAuthorBlockDesktopBlock>
          <Headline>
            {provider.headline({
              hideTag: true,
            })}
          </Headline>
        </StyledHeadline>
        <StyledSubHeadline>{provider.subHeadline()}</StyledSubHeadline>
        <Author>{provider.authorBlock()}</Author>
      </HeaderContainer>
    </Container>
  );
};
